import React, { useMemo, useState } from "react"
import Chart from "react-apexcharts"
import { Modal, ModalBody, ModalHeader } from "reactstrap"

const fmtGBP = val => {
  const n = Number(val)
  const abs = Math.abs(n)
  const prefix = n < 0 ? "-£" : "£"
  if (abs >= 1000000) return `${prefix}${(abs / 1000000).toFixed(1)}M`
  if (abs >= 1000) return `${prefix}${Math.round(abs / 1000)}k`
  return `${prefix}${Math.round(abs)}`
}

const CategoryContributionChart = ({ data = [] }) => {
  const [modal, setModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [modalData, setModalData] = useState([])

  const categories = useMemo(() => {
    const positives = data
      .filter(c => Number(c.totalNetMarginContribution) >= 0)
      .sort(
        (a, b) =>
          Number(b.totalNetMarginContribution) -
          Number(a.totalNetMarginContribution)
      )
    const negatives = data
      .filter(c => Number(c.totalNetMarginContribution) < 0)
      .sort(
        (a, b) =>
          Number(a.totalNetMarginContribution) -
          Number(b.totalNetMarginContribution)
      )
    return [...positives, ...negatives]
  }, [data])

  const handleBarClick = (_event, _ctx, config) => {
    const idx = config.dataPointIndex
    if (idx < 0 || idx === undefined) return
    const cat = categories[idx]
    if (!cat) return
    setSelectedCategory(cat)
    setModalData(cat.data || [])
    setModal(true)
  }

  const chartHeight = Math.max(420, categories.length * 52)
  const rowHeightApprox = chartHeight / categories.length

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      background: "transparent",
      events: { dataPointSelection: handleBarClick },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: "60%",
        distributed: true,
      },
    },
    colors: categories.map(c =>
      c.totalNetMarginContribution >= 0 ? "#22C55E" : "#EF5350"
    ),
    dataLabels: {
      enabled: true,
      textAnchor: "end",
      formatter: val => fmtGBP(val),
      style: { fontSize: "11px", fontWeight: 700, colors: ["#1a1a2e"] },
      offsetX: 40,
      background: {
        enabled: true,
        foreColor: "#fff",
        padding: 4,
        borderRadius: 4,
        borderWidth: 0,
        opacity: 0.85,
        dropShadow: { enabled: false },
      },
    },
    xaxis: {
      categories: categories.map(i => i.categoryName),
      labels: {
        formatter: val => fmtGBP(Number(val)),
        style: { fontSize: "11px", colors: "#64748b" },
      },
    },
    yaxis: {
      labels: {
        style: { fontSize: "11px", fontWeight: 600, colors: "#1e293b" },
      },
    },
    annotations: {
      xaxis: [
        {
          x: 0,
          strokeDashArray: 4,
          borderColor: "#333",
          borderWidth: 2,
          label: {
            borderColor: "#333",
            style: {
              color: "#333",
              background: "#fff",
              fontSize: "10px",
              fontWeight: 700,
            },
            text: "0",
            position: "top",
          },
        },
      ],
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },

    legend: { show: false },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const cat = categories[dataPointIndex]
        if (!cat) return ""
        const color =
          cat.totalNetMarginContribution >= 0 ? "#22C55E" : "#EF5350"
        return `<div style="padding:10px 14px;background:#1e293b;border-radius:8px;min-width:220px;color:#f1f5f9;font-size:12px">
          <div style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px">Category</div>
          <div style="font-weight:600;margin-bottom:4px">${
            cat.categoryName
          }</div>
          <div style="font-weight:800;color:${color};font-size:14px">${fmtGBP(
          cat.totalNetMarginContribution
        )}</div>
          `
      },
    },
    legend: { show: false },
  }

  const series = [
    {
      name: "Net Margin Contribution",
      data: categories.map(c => Math.round(c.totalNetMarginContribution)),
    },
  ]

  const positiveTotal = categories
    .filter(c => c.totalNetMarginContribution > 0)
    .reduce((s, c) => s + Number(c.totalNetMarginContribution), 0)

  const negativeTotal = categories
    .filter(c => c.totalNetMarginContribution < 0)
    .reduce((s, c) => s + Number(c.totalNetMarginContribution), 0)

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "20px 20px 16px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h4
              style={{
                fontSize: "15px",
                fontWeight: 700,
                margin: 0,
                color: "#1a1a2e",
              }}
            >
              Net Margin by Category
            </h4>
          </div>

          {/* Summary badges */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "10px",
                padding: "6px 14px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#15803d",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Positive
              </span>
              <span
                style={{ fontSize: "14px", fontWeight: 800, color: "#15803d" }}
              >
                {fmtGBP(positiveTotal)}
              </span>
            </div>
            <div
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: "10px",
                padding: "6px 14px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#be123c",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Negative
              </span>
              <span
                style={{ fontSize: "14px", fontWeight: 800, color: "#be123c" }}
              >
                {fmtGBP(negativeTotal)}
              </span>
            </div>
            {/* <div
              style={{
                background:
                  positiveTotal + negativeTotal >= 0 ? "#f0fdf4" : "#fff1f2",
                border: `1px solid ${
                  positiveTotal + negativeTotal >= 0 ? "#86efac" : "#fca5a5"
                }`,
                borderRadius: "10px",
                padding: "6px 14px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color:
                    positiveTotal + negativeTotal >= 0 ? "#15803d" : "#be123c",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Net
              </span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 800,
                  color:
                    positiveTotal + negativeTotal >= 0 ? "#15803d" : "#be123c",
                }}
              >
                {fmtGBP(positiveTotal + negativeTotal)}
              </span>
            </div> */}
          </div>
        </div>

        <Chart
          options={options}
          series={series}
          type="bar"
          height={chartHeight}
        />

        <div
          style={{
            textAlign: "center",
            fontSize: "12px",
            fontWeight: 700,
            color: "#374151",
            marginTop: "4px",
          }}
        >
          Net Margin Contribution (£)
        </div>
      </div>

      {/* Drill-down Modal */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="xl" centered>
        <ModalHeader
          toggle={() => setModal(false)}
          style={{ borderBottom: "1px solid #f1f5f9", padding: "14px 20px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
              {selectedCategory?.categoryName} Customer &amp; Product Breakdown
            </span>
            {selectedCategory && (
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 800,
                  color:
                    selectedCategory.totalNetMarginContribution >= 0
                      ? "#15803d"
                      : "#be123c",
                  background:
                    selectedCategory.totalNetMarginContribution >= 0
                      ? "#f0fdf4"
                      : "#fff1f2",
                  border: `1px solid ${
                    selectedCategory.totalNetMarginContribution >= 0
                      ? "#bbf7d0"
                      : "#fecdd3"
                  }`,
                  borderRadius: "8px",
                  padding: "2px 10px",
                }}
              >
                {fmtGBP(selectedCategory.totalNetMarginContribution)}
              </span>
            )}
          </div>
        </ModalHeader>
        <ModalBody
          style={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "60vh",
            overflow: "hidden",
          }}
        >
          {modalData.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px",
                color: "#94a3b8",
                fontSize: "14px",
              }}
            >
              No data available
            </div>
          ) : (
            <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "2px solid #e2e8f0",
                    }}
                  >
                    {[
                      { label: "Customer", align: "left" },
                      { label: "Product", align: "left" },
                      { label: "Net Margin %", align: "right" },
                      { label: "Net Margin Contribution", align: "right" },
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "10px 16px",
                          textAlign: h.align,
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#64748b",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modalData.map((row, i) => {
                    const color =
                      Number(row.netMarginContribution) >= 0
                        ? "#15803d"
                        : "#be123c"
                    return (
                      <tr
                        key={i}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: i % 2 === 0 ? "#fff" : "#fafbfc",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 16px",
                            color: "#1e293b",
                            fontWeight: 600,
                          }}
                        >
                          {row.customerName}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            color: "#374151",
                            fontWeight: 500,
                          }}
                        >
                          {row.productName}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            fontWeight: 700,
                            color,
                          }}
                        >
                          {Number(row.netMarginPercent).toFixed(1)}%
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            fontWeight: 700,
                            color,
                          }}
                        >
                          {fmtGBP(row.netMarginContribution)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      borderTop: "2px solid #e2e8f0",
                      background: "#f8fafc",
                    }}
                  >
                    <td
                      colSpan={3}
                      style={{
                        padding: "10px 16px",
                        fontWeight: 700,
                        fontSize: "13px",
                        color: "#374151",
                        textAlign: "right",
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        textAlign: "right",
                        fontWeight: 800,
                        fontSize: "14px",
                        color:
                          modalData.reduce(
                            (s, r) => s + Number(r.netMarginContribution),
                            0
                          ) >= 0
                            ? "#15803d"
                            : "#be123c",
                      }}
                    >
                      {fmtGBP(
                        modalData.reduce(
                          (s, r) => s + Number(r.netMarginContribution),
                          0
                        )
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default CategoryContributionChart
